import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { FiLogOut, FiMenu, FiX } from "react-icons/fi";
import logo from "../assets/logo.png";

function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [userName, setUserName] = useState("");
    const [photoURL, setPhotoURL] = useState(null);
    const [photoErr, setPhotoErr] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const currentUser = auth.currentUser;
        if (currentUser) {
            setUserName(currentUser.displayName || currentUser.email || "Admin");
            setPhotoURL(currentUser.photoURL || null);
        }
    }, []);

    // Close the mobile drawer whenever the route changes
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await signOut(auth);
        navigate("/login");
    };

    const initials = (userName || "A").split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
    const onProfile = location.pathname === "/profile";

    return (
        <>
            {/* ── Mobile top bar ── */}
            <div className="navbar-mobile-topbar">
                <button
                    className="navbar-mobile-toggle"
                    onClick={() => setMobileOpen(o => !o)}
                    aria-label={mobileOpen ? "Close menu" : "Open menu"}
                >
                    {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
                </button>
                <div className="navbar-mobile-brand">
                    <img src={logo} alt="Kubaya" className="navbar-logo-img" />
                    <span className="navbar-brand-name">Kubaya</span>
                </div>
            </div>

            {/* ── Backdrop (mobile drawer open) ── */}
            {mobileOpen && (
                <div className="navbar-backdrop" onClick={() => setMobileOpen(false)} />
            )}

            <nav className={`navbar${mobileOpen ? " navbar-open" : ""}`}>
                {/* ── Logo ── */}
                <div className="navbar-logo-area">
                    <img src={logo} alt="Kubaya" className="navbar-logo-img" />
                    <span className="navbar-brand-name">Kubaya</span>
                </div>

                {/* ── Nav links ── */}
                <div className="navbar-links">
                    <NavLink to="/" end>Dashboard</NavLink>
                    <NavLink to="/lessons">Lessons</NavLink>
                    <NavLink to="/flashcards">Flashcards</NavLink>
                    <NavLink to="/users">Users</NavLink>
                    <NavLink to="/announcements">Announcements</NavLink>
                    <NavLink to="/reports">Reports</NavLink>
                    <NavLink to="/adminusers">Admin Users</NavLink>
                    <NavLink to="/premiumusers">Premium Users</NavLink>
                    <NavLink to="/questionnaire">Questionnaire</NavLink>
                    <NavLink to="/waitlist">Waitlist</NavLink>
                    <NavLink to="/betasignups">Beta Signups</NavLink>
                </div>

            {/* ── Profile card ── */}
            <div style={{
                margin: "10px 10px",
                borderRadius: 16,
                border: `1.5px solid ${onProfile ? "#2a9d8f" : "#E0E0E0"}`,
                borderBottomWidth: onProfile ? 3 : 1.5,
                borderBottomColor: onProfile ? "#258D80" : "#E0E0E0",
                background: onProfile ? "#f0faf9" : "#fff",
                overflow: "hidden",
                flexShrink: 0,
                transition: "border-color 0.15s ease, background 0.15s ease",
            }}>
                {/* Profile button */}
                <button
                    onClick={() => navigate("/profile")}
                    title="My Profile"
                    style={{
                        width: "100%", background: "none", border: "none",
                        padding: "11px 12px",
                        display: "flex", alignItems: "center", gap: 10,
                        cursor: "pointer", textAlign: "left",
                    }}
                    onMouseEnter={e => { if (!onProfile) e.currentTarget.style.background = "rgba(42,157,143,0.06)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
                >
                    {/* Avatar */}
                    {photoURL && !photoErr ? (
                        <img
                            src={photoURL}
                            alt={userName}
                            onError={() => setPhotoErr(true)}
                            style={{
                                width: 34, height: 34, borderRadius: "50%", objectFit: "cover",
                                border: `2px solid ${onProfile ? "#2a9d8f" : "#E0E0E0"}`,
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <div style={{
                            width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                            background: onProfile ? "#2a9d8f" : "#e9f7f6",
                            color: onProfile ? "#fff" : "#2a9d8f",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 13, fontWeight: 800,
                            border: `2px solid ${onProfile ? "#258D80" : "#2a9d8f30"}`,
                            transition: "background 0.15s ease, color 0.15s ease",
                        }}>
                            {initials}
                        </div>
                    )}

                    {/* Name + role */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                            fontWeight: 700, fontSize: 13,
                            color: onProfile ? "#2a9d8f" : "#000",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            fontFamily: "'Quicksand', sans-serif",
                            lineHeight: 1.2, marginBottom: 2,
                        }}>
                            {userName}
                        </div>
                        <div style={{
                            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            color: onProfile ? "#2a9d8f" : "#aaa",
                        }}>
                            Administrator
                        </div>
                    </div>
                </button>

                {/* Divider */}
                <div style={{ height: 1, background: onProfile ? "#2a9d8f30" : "#f0f0f0", margin: "0 10px" }} />

                {/* Sign out button */}
                <button
                    onClick={handleLogout}
                    title="Sign out"
                    style={{
                        width: "100%", background: "none", border: "none",
                        padding: "9px 12px",
                        display: "flex", alignItems: "center", gap: 7,
                        cursor: "pointer",
                        color: "#606060",
                        fontSize: 12, fontWeight: 700,
                        fontFamily: "'Quicksand', sans-serif",
                        textTransform: "uppercase", letterSpacing: "0.05em",
                        transition: "color 0.15s ease",
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.color = "#e76f51";
                        e.currentTarget.style.background = "rgba(231,111,81,0.06)";
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.color = "#606060";
                        e.currentTarget.style.background = "none";
                    }}
                >
                    <FiLogOut size={13} /> Sign Out
                </button>
            </div>
        </nav>
        </>
    );
}

export default Navbar;
